import { Outlet, NavLink } from 'react-router-dom';
import { useCurrentCentro } from '@/hooks/useCentroVisits';

interface IconProps {
  'aria-hidden'?: boolean | 'true' | 'false';
}

interface NavSlot {
  to: string;
  label: string;
  Icon: (props: IconProps) => JSX.Element;
  end?: boolean;
  /** When true, the tab is only enabled while the participant is checked into a Centro. */
  requiresCentro?: boolean;
}

const LEFT_SLOTS: ReadonlyArray<NavSlot> = [
  { to: '/', label: 'Início', Icon: HomeIcon, end: true },
  { to: '/reservations', label: 'Reservas', Icon: BookmarkIcon, requiresCentro: true },
];

const RIGHT_SLOTS: ReadonlyArray<NavSlot> = [
  { to: '/percurso', label: 'Percurso', Icon: RouteIcon },
  { to: '/profile', label: 'Perfil', Icon: UserIcon },
];

export default function MobileLayout() {
  const currentCentroQuery = useCurrentCentro();
  const hasActiveCentro = !!currentCentroQuery.data;

  return (
    <div className="relative min-h-screen bg-background pb-28">
      <a href="#main-content" className="skip-link">
        Saltar para o conteúdo principal
      </a>

      <main id="main-content" className="safe-area-inset px-4 py-4">
        <Outlet />
      </main>

      <nav className="nav-shell" aria-label="Main navigation">
        {LEFT_SLOTS.map((slot) => renderTab(slot, hasActiveCentro))}
        <div className="nav-scan">
          <NavLink to="/scan" className="nav-fab" aria-label="Scan QR">
            <ScanIcon aria-hidden="true" />
          </NavLink>
        </div>
        {RIGHT_SLOTS.map((slot) => renderTab(slot, hasActiveCentro))}
      </nav>
    </div>
  );
}

function renderTab(slot: NavSlot, hasActiveCentro: boolean) {
  const { to, label, Icon, end, requiresCentro } = slot;
  const disabled = requiresCentro === true && !hasActiveCentro;

  if (disabled) {
    return (
      <span
        key={to}
        className="nav-item nav-item--disabled"
        aria-disabled="true"
        aria-label={`${label} (faça check-in num Centro para activar)`}
        role="link"
        tabIndex={-1}
      >
        <Icon aria-hidden="true" />
        <span className="nav-label">{label}</span>
      </span>
    );
  }

  return (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) =>
        `nav-item ${isActive ? 'nav-item--active' : ''}`
      }
    >
      {({ isActive }) => (
        <>
          <Icon aria-hidden="true" />
          <span
            className="nav-label"
            aria-current={isActive ? 'page' : undefined}
          >
            {label}
          </span>
        </>
      )}
    </NavLink>
  );
}

function HomeIcon(props: IconProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={props['aria-hidden']}
    >
      <path d="M3 12l9-9 9 9" />
      <path d="M5 10v10a1 1 0 001 1h3v-6h6v6h3a1 1 0 001-1V10" />
    </svg>
  );
}

function BookmarkIcon(props: IconProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={props['aria-hidden']}
    >
      <path d="M6 4a2 2 0 012-2h8a2 2 0 012 2v18l-6-3-6 3V4z" />
    </svg>
  );
}

function RouteIcon(props: IconProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={props['aria-hidden']}
    >
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="5" r="2" />
      <path d="M8 19h8.5a3.5 3.5 0 000-7h-9a3.5 3.5 0 010-7H16" />
    </svg>
  );
}

function UserIcon(props: IconProps) {
  return (
    <svg
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={props['aria-hidden']}
    >
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0116 0" />
    </svg>
  );
}

function ScanIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden={props['aria-hidden']}
    >
      <path d="M9.5 6.5v3h-3v-3h3M11 5H5v6h6V5zm-1.5 9.5v3h-3v-3h3M11 13H5v6h6v-6zm6.5-6.5v3h-3v-3h3M19 5h-6v6h6V5zm-6 8h1.5v1.5H13V13zm1.5 1.5H16V16h-1.5v-1.5zM16 13h1.5v1.5H16V13zm-3 3h1.5v1.5H13V16zm1.5 1.5H16V19h-1.5v-1.5zM16 16h1.5v1.5H16V16zm1.5-1.5H19V16h-1.5v-1.5zm0 3H19V19h-1.5v-1.5z" />
    </svg>
  );
}
